import {
  Component,
  OnInit,
  OnDestroy,
  TemplateRef,
  ViewChild,
  ElementRef,
  ViewEncapsulation,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, BehaviorSubject, of, switchMap } from 'rxjs';
import { takeUntil, finalize, tap } from 'rxjs/operators';
import {
  FormBuilder,
  FormGroup,
  Validators,
  FormControl,
  AbstractControl,
  ValidatorFn,
} from '@angular/forms';
import { ServiceTemplateService } from '../../../../core/services/service-template.service';
import { ServiceChangeRequestService } from '../../../../core/services/service-change-request.service';
import {
  IServiceTemplate,
  IUpdateServiceTemplateRequest,
  ServiceTemplateStatus,
} from '../../../../core/models/service-template/service-template.model';
import { IServiceCategory } from '../../../../core/models/service-category.model';
import { IServiceChangeRequest } from '../../../../core/models/service-change-request.model';
import { IServiceConfigStep } from '../../../../core/models/service-change-request-config-steps-forms.model';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { DefaultEmptyFormData } from '../../../../core/models/form-builder-fields-data.model';
import { FormBuilderService } from '../../../../core/services/form-builder.service';
import {
  ToastService,
  ExtractHttpErrorResponseCodeAndMessage,
  IHttpErrorResponseCodeAndMessage,
  IFilterAndPaginationFieldsBase,
  FilterFieldsToQueryParamsBuilder,
  updateApiFilter,
  ExtractHttpErrorResponseMessage,
  IHttpSingleDataResponse,
} from '@irembo-andela/irembogov3-common';
import { HttpErrorResponse } from '@angular/common/http';
import { CdkStepper, StepperSelectionEvent } from '@angular/cdk/stepper';

interface IFilterFields extends IFilterAndPaginationFieldsBase {
  name: string | null;
  keywords: string | null;
}

@Component({
  selector: 'irembogov-edit-template',
  templateUrl: './edit-template.component.html',
  styleUrls: ['./edit-template.component.scss'],
  providers: [{ provide: CdkStepper }],
  encapsulation: ViewEncapsulation.None,
})
export class EditTemplateComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  templateId!: string;
  template!: IServiceTemplate;
  isLoading = false;
  isUpdating = false;
  isLoadingDetails = false;
  loadError = false;
  currentConfigStep = 0;
  showFormPreview = false;

  // Form templates
  @ViewChild('overview', { static: true }) overviewTemplate!: TemplateRef<any>;
  @ViewChild('formbuilder', { static: false })
  formbuilderTemplate!: TemplateRef<any>;
  @ViewChild('pricing', { static: false }) pricingTemplate!: TemplateRef<any>;
  @ViewChild('workflow', { static: false }) workflowTemplate!: TemplateRef<any>;
  @ViewChild('officerform', { static: false })
  officerformTemplate!: TemplateRef<any>;
  @ViewChild('translation', { static: false })
  translationTemplate!: TemplateRef<any>;
  @ViewChild('journeymapping', { static: false })
  journeymappingTemplate!: TemplateRef<any>;

  // UI state
  persistenceStatus = 'Ready to edit template';
  showHtmlCode = false;
  quillConfig = {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      ['link'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      ['clean'],
    ],
  };

  configSteps: IServiceConfigStep[] = [
    {
      label: 'Overview',
      key: 'overview',
      icon: 'fas fa-info-circle',
      isComplete: false,
      hasError: false,
    },
    {
      label: 'Form Builder',
      key: 'formbuilder',
      icon: 'fas fa-border-none',
      isComplete: false,
      hasError: false,
    },
    {
      label: 'Pricing',
      key: 'pricing',
      icon: 'fas fa-money-bill-1-wave',
      isComplete: false,
      hasError: false,
    },
    {
      label: 'Workflow',
      key: 'workflow',
      icon: 'fas fa-diagram-project',
      isComplete: false,
      hasError: false,
    },
    {
      label: 'Officer Form',
      key: 'officerform',
      icon: 'fas fa-file',
      isComplete: false,
      hasError: false,
    },
    {
      label: 'Translation',
      key: 'translation',
      icon: 'fas fa-language',
      isComplete: false,
      hasError: false,
    },
  ];

  trafficClasses = [
    { value: 'regular', label: 'Regular' },
    { value: 'high', label: 'High' },
    { value: 'critical', label: 'Critical' },
  ];

  serviceTypes = [
    { value: 'NORMAL', label: 'Normal' },
    { value: 'JOURNEY', label: 'Journey' },
  ];

  // Form data
  overviewForm!: FormGroup;
  categories: IServiceCategory[] = [];
  serviceCategoriesBuffer: IServiceCategory[] = [];
  isLoadingServiceCategories = false;

  _filterObject: BehaviorSubject<IFilterFields> =
    new BehaviorSubject<IFilterFields>({
      page: 0,
      size: 8,
      name: '',
      keywords: '',
    });

  // Template data properties (equivalent to change request data)
  mockChangeRequest!: IServiceChangeRequest; // Mock change request for template components
  templateJsonFormData!: Record<string, FormlyFieldConfig[]>;
  templateJsonWorkflowData!: Record<string, unknown>;
  templateTranslationsData!: Record<string, Record<string, unknown>>;
  templateFormFields!: any;
  templatePricingData!: Record<string, unknown>;
  templateThirdPartyFormData!: Record<string, unknown>;
  templateJourneyMappingData!: Record<string, unknown>;

  readyForSaveAndPublish = false;
  ServiceTemplateStatus = ServiceTemplateStatus;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private serviceTemplateService: ServiceTemplateService,
    private serviceChangeRequestService: ServiceChangeRequestService,
    private formBuilderService: FormBuilderService,
    private toastService: ToastService
  ) {
    this.initializeOverviewForm();
  }

  ngOnInit(): void {
    this._filterObject
      .asObservable()
      .pipe(
        switchMap((filter: IFilterFields) => {
          FilterFieldsToQueryParamsBuilder<IFilterFields>(filter);
          this.getServiceCategoriesByPage();
          return of();
        })
      )
      .subscribe();

    this.route.params.pipe(takeUntil(this.destroy$)).subscribe(params => {
      this.templateId = params['id'];
      if (this.templateId) {
        this.loadTemplate();
      }
    });

    this.loadCategories();
    this.initializeEmptyData();
    this.formBuilderService.startFormBuilderConfigurationSubjects();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.formBuilderService.completeFormBuilderConfigurationSubjects();
  }

  private initializeOverviewForm(): void {
    this.overviewForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(3)]],
      description: [''],
      categoryId: ['', Validators.required],
      trafficClass: ['regular', Validators.required],
      serviceType: ['NORMAL', Validators.required],
      applicantType: new FormControl<string[]>(
        [],
        this.minSelectedCheckboxes(1)
      ),
      knowledgeBaseUrl: new FormControl<string>('', [this.urlValidator()]),
    });
  }

  loadTemplate(): void {
    this.isLoading = true;
    this.loadError = false;

    this.serviceTemplateService
      .getTemplateById(this.templateId)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.isLoading = false)),
        tap(() => {
          this.getTemplateFormDefinition();
          this.getTemplateWorkflowNextStepsProcessingTime();
          this.getTemplatePricingData();
          this.getTemplateTranslationsData();
          this.getTemplateThirdPartyFormDefinition();
        })
      )
      .subscribe({
        next: (response: IHttpSingleDataResponse<IServiceTemplate>) => {
          this.template = response.data;
          this.persistenceStatus = `Template loaded: ${this.template.name}`;
          this.updateConfigSteps();
          this.populateOverviewForm();
          this.createMockChangeRequest();
        },
        error: () => {
          this.loadError = true;
        },
      });
  }

  private populateOverviewForm(): void {
    if (this.template) {
      // Transform applicantType back to array format
      let applicantTypeArray: string[] = [];
      switch (this.template.applicantType) {
        case 'INDIVIDUAL':
          applicantTypeArray = ['INDIVIDUAL'];
          break;
        case 'BUSINESS':
          applicantTypeArray = ['BUSINESS'];
          break;
        case 'BOTH':
          applicantTypeArray = ['INDIVIDUAL', 'BUSINESS'];
          break;
      }

      this.overviewForm.patchValue({
        name: this.template.name,
        description: this.template.description,
        categoryId: this.template.categoryId,
        trafficClass: this.template.trafficClass,
        serviceType: this.template.serviceType,
        applicantType: applicantTypeArray,
        knowledgeBaseUrl: this.template.knowledgeBaseUrl || '',
      });
      this.configSteps[0].isComplete = true;
    }
  }

  updateConfigSteps(): void {
    if (this.template?.serviceType === 'JOURNEY') {
      // Check if the journey mapping step already exists
      const journeyMappingStepExists = this.configSteps.some(
        step => step.key === 'journeymapping'
      );

      if (!journeyMappingStepExists) {
        // Add the journey mapping step before the form builder step
        const formBuilderIndex = this.configSteps.findIndex(
          step => step.key === 'formbuilder'
        );

        if (formBuilderIndex !== -1) {
          const journeyMappingStep: IServiceConfigStep = {
            label: 'Journey Mapping',
            key: 'journeymapping',
            icon: 'fas fa-map',
            isComplete: false,
            hasError: false,
          };

          this.configSteps.splice(formBuilderIndex, 0, journeyMappingStep);
        }
      }
    } else {
      // Remove the journey mapping step if it exists
      const index = this.configSteps.findIndex(
        step => step.key === 'journeymapping'
      );
      if (index !== -1) {
        this.configSteps.splice(index, 1);
      }
    }
  }

  // Template data loading methods
  getTemplateFormDefinition(): void {
    if (!this.templateId) return;

    this.isLoadingDetails = true;
    this.serviceTemplateService
      .getTemplateFormDefinition(this.templateId)
      .pipe(finalize(() => (this.isLoadingDetails = false)))
      .subscribe({
        next: (
          response: IHttpSingleDataResponse<Record<string, FormlyFieldConfig[]>>
        ) => {
          this.templateJsonFormData = response.data;
          this.templateFormFields = response.data;
          this.formBuilderService.loadJsonFormDataForPreview(
            this.templateJsonFormData
          );
        },
        error: () => {
          this.templateJsonFormData = DefaultEmptyFormData;
        },
      });
  }

  getTemplateWorkflowNextStepsProcessingTime(): void {
    if (!this.templateId) return;

    this.isLoadingDetails = true;
    this.serviceTemplateService
      .getTemplateWorkflowNextStepsProcessingTime(this.templateId)
      .pipe(finalize(() => (this.isLoadingDetails = false)))
      .subscribe({
        next: (response: IHttpSingleDataResponse<Record<string, unknown>>) => {
          this.templateJsonWorkflowData = response.data;
        },
        error: () => {
          this.templateJsonWorkflowData = {};
        },
      });
  }

  getTemplatePricingData(): void {
    if (!this.templateId) return;

    this.isLoadingDetails = true;
    this.serviceTemplateService
      .getTemplatePricing(this.templateId)
      .pipe(finalize(() => (this.isLoadingDetails = false)))
      .subscribe({
        next: (response: IHttpSingleDataResponse<Record<string, unknown>>) => {
          this.templatePricingData = response.data;
        },
        error: () => {
          this.templatePricingData = {};
        },
      });
  }

  getTemplateTranslationsData(): void {
    if (!this.templateId) return;

    this.isLoadingDetails = true;
    this.serviceTemplateService
      .getTemplateTranslation(this.templateId)
      .pipe(finalize(() => (this.isLoadingDetails = false)))
      .subscribe({
        next: (
          response: IHttpSingleDataResponse<
            Record<string, Record<string, unknown>>
          >
        ) => {
          this.templateTranslationsData = response.data;
        },
        error: () => {
          this.templateTranslationsData = {};
        },
      });
  }

  getTemplateThirdPartyFormDefinition(): void {
    if (!this.templateId) return;

    this.isLoadingDetails = true;
    this.serviceTemplateService
      .getTemplateThirdPartyFormDefinition(this.templateId)
      .pipe(finalize(() => (this.isLoadingDetails = false)))
      .subscribe({
        next: (
          response: IHttpSingleDataResponse<
            Record<string, Record<string, unknown>>
          >
        ) => {
          this.templateThirdPartyFormData = response.data;
        },
        error: () => {
          this.templateThirdPartyFormData = {};
        },
      });
  }

  private loadCategories(): void {
    this.serviceChangeRequestService
      .getAllServiceCategories()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.categories = response.data || [];
        },
        error: () => {
          this.categories = [];
        },
      });
  }

  getServiceCategoriesByPage() {
    this.isLoadingServiceCategories = true;
    this.serviceChangeRequestService
      .getAllServiceCategories()
      .pipe(finalize(() => (this.isLoadingServiceCategories = false)))
      .subscribe({
        next: response => {
          this.serviceCategoriesBuffer = response?.data;
        },
        error: err => {
          const errorMessage = ExtractHttpErrorResponseMessage(
            err,
            'Error getting services'
          );
          this.toastService.show({ body: errorMessage, type: 'error' });
        },
      });
  }

  onSearchServiceCategory(query: { term: string }) {
    updateApiFilter<IFilterFields>(this._filterObject, {
      page: 0,
      name: query?.term,
    });
  }

  private initializeEmptyData(): void {
    this.templateJsonFormData = DefaultEmptyFormData;
    this.templateJsonWorkflowData = {};
    this.templateTranslationsData = {};
    this.templatePricingData = {};
    this.templateThirdPartyFormData = {};
    this.templateJourneyMappingData = {};
  }

  getCurrentTemplate(index: number): TemplateRef<ElementRef> {
    if (index >= 0 && index < this.configSteps.length) {
      const templateKey = `${this.configSteps[index].key}Template`;
      return this[
        templateKey as keyof EditTemplateComponent
      ] as TemplateRef<ElementRef>;
    }
    return this.overviewTemplate;
  }

  onOverviewFormSubmitted(): void {
    if (this.overviewForm.valid) {
      this.updateTemplateBasicInfo();
    }
  }

  private updateTemplateBasicInfo(): void {
    this.isUpdating = true;

    // Transform applicantType array to enum value
    const applicantTypeArray: string[] = this.overviewForm.value.applicantType;
    let applicantType: 'INDIVIDUAL' | 'BUSINESS' | 'BOTH' = 'INDIVIDUAL';

    if (
      applicantTypeArray.includes('INDIVIDUAL') &&
      applicantTypeArray.includes('BUSINESS')
    ) {
      applicantType = 'BOTH';
    } else if (applicantTypeArray.includes('INDIVIDUAL')) {
      applicantType = 'INDIVIDUAL';
    } else if (applicantTypeArray.includes('BUSINESS')) {
      applicantType = 'BUSINESS';
    }

    const templateData = {
      ...this.overviewForm.value,
      applicantType: applicantType,
    };

    this.serviceTemplateService
      .updateTemplate(this.templateId, templateData)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.isUpdating = false))
      )
      .subscribe({
        next: response => {
          this.template = response.data;
          this.createMockChangeRequest();
          this.updateConfigSteps();
          this.configSteps[0].isComplete = true;
          this.persistenceStatus = 'Template updated successfully';
          // Check if we should enable save and publish
          if (this.currentConfigStep >= this.configSteps.length - 1) {
            this.readyForSaveAndPublish = true;
          }
          this.toastService.show({
            body: 'Template updated successfully',
            type: 'success',
          });
          this.goToNextStep();
        },
        error: (error: HttpErrorResponse) => {
          const errorCodeAndMessage: IHttpErrorResponseCodeAndMessage =
            ExtractHttpErrorResponseCodeAndMessage(
              error,
              'Failed to update template'
            );
          this.toastService.show({
            body: errorCodeAndMessage.message,
            type: 'error',
          });
        },
      });
  }

  onStepFormSubmitted(isValid: boolean): void {
    this.configSteps[this.currentConfigStep].isComplete = isValid;
    this.handleNextStepValidity(isValid);
  }

  onWorkflowStepSubmitted(data: Record<string, unknown> | undefined): void {
    this.configSteps[this.currentConfigStep].isComplete = data !== undefined;
    this.handleNextStepValidity(data !== undefined);
    this.templateJsonWorkflowData = data ?? {};
  }

  onJourneyMappingStepSubmitted(isValid: boolean): void {
    this.getTemplateFormDefinition();
    this.configSteps[this.currentConfigStep].isComplete = isValid;
    this.handleNextStepValidity(isValid);
  }

  handleNextStepValidity(stepIsValid: boolean): void {
    if (stepIsValid) {
      // Reload template data if needed
      if (this.templateId) {
        this.loadTemplate();
      }
      this.goToNextStep();
    }
  }

  goToNextStep(): void {
    if (this.currentConfigStep < this.configSteps.length - 1) {
      this.currentConfigStep++;
      return;
    }
    if (
      this.currentConfigStep >= this.configSteps.length - 1 &&
      this.template
    ) {
      this.readyForSaveAndPublish = true;
    }
  }

  goToPreviousStep(): void {
    if (this.currentConfigStep > 0) {
      this.currentConfigStep--;
    }
  }

  toggleFormPreview(event: boolean): void {
    if (!this.template || !this.templateJsonFormData) {
      this.toastService.show({
        body: 'Please create the template first before previewing the form',
        type: 'warning',
      });
      return;
    }
    this.showFormPreview = event;
  }

  onStepChange(index: number): void {
    // Allow navigation only to completed steps or the next step
    if (
      index <= this.currentConfigStep ||
      this.configSteps[index - 1]?.isComplete
    ) {
      this.currentConfigStep = index;
    }
  }

  onStepperChange(event: any): void {
    this.currentConfigStep = event.selectedIndex;
  }

  saveAsDraft(): void {
    this.toastService.show({
      body: 'Template saved as draft',
      type: 'success',
    });
    this.router.navigate(['/services/templates']);
  }

  publishTemplate(): void {
    this.isUpdating = true;

    this.serviceTemplateService
      .publishTemplate(this.templateId)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.isUpdating = false))
      )
      .subscribe({
        next: () => {
          this.toastService.show({
            body: 'Template published successfully',
            type: 'success',
          });
          this.router.navigate(['/services/templates']);
        },
        error: (error: HttpErrorResponse) => {
          const errorCodeAndMessage: IHttpErrorResponseCodeAndMessage =
            ExtractHttpErrorResponseCodeAndMessage(
              error,
              'Failed to publish template'
            );
          this.toastService.show({
            body: errorCodeAndMessage.message,
            type: 'error',
          });
        },
      });
  }

  cancel(): void {
    this.router.navigate(['/services/templates']);
  }

  canEdit(): boolean {
    return this.template?.status === ServiceTemplateStatus.DRAFT;
  }

  // Applicant type checkbox handlers - exact pattern from configuration-metadata-overview-form
  onCheckboxChange(event: any) {
    const applicantTypeControl = this.overviewForm.get(
      'applicantType'
    ) as FormControl;
    const value = event.target.value;
    let selectedValues = applicantTypeControl.value as string[];

    if (event.target.checked) {
      selectedValues.push(value);
    } else {
      selectedValues = selectedValues.filter(v => v !== value);
    }

    applicantTypeControl.setValue(selectedValues);
    applicantTypeControl.updateValueAndValidity();
  }

  // HTML Editor methods
  toggleHtmlEditor(): void {
    this.showHtmlCode = !this.showHtmlCode;
  }

  getWordCount(text: string): number {
    if (!text) return 0;
    // Remove HTML tags and count words
    const plainText = text.replace(/<[^>]*>/g, '').trim();
    return plainText ? plainText.split(/\s+/).length : 0;
  }

  // Form validators
  private minSelectedCheckboxes(min: number): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const selectedValues = control.value as string[];
      return selectedValues.length >= min ? null : { required: true };
    };
  }

  private urlValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (!control.value) {
        // If the field is empty, it's valid since it's optional
        return null;
      }

      try {
        const url = new URL(control.value);
        // Check if protocol is http or https
        if (url.protocol !== 'http:' && url.protocol !== 'https:') {
          return { invalidProtocol: true };
        }
        return null;
      } catch (error) {
        // If URL constructor throws an error, the URL is invalid
        return { invalidUrl: true };
      }
    };
  }

  private createMockChangeRequest(): void {
    // Create a mock change request object for template configuration components
    this.mockChangeRequest = {
      id: this.templateId || 'temp-id',
      serviceName:
        this.template?.name ||
        this.overviewForm.value.name ||
        'Template Service',
      serviceSummary:
        this.template?.description || this.overviewForm.value.description || '',
      categoryId:
        this.template?.categoryId || this.overviewForm.value.categoryId || '',
      serviceId: '',
      trafficClass:
        this.template?.trafficClass ||
        this.overviewForm.value.trafficClass ||
        'NORMAL',
      applicantType:
        this.template?.applicantType ||
        this.overviewForm.value.applicantType ||
        'INDIVIDUAL',
      serviceType:
        this.template?.serviceType ||
        this.overviewForm.value.serviceType ||
        'NORMAL',
      status: 'IN_DEVELOPMENT' as any,
      serviceRequirementDocument: {
        contentType: '',
        file: '',
      },
      knowledgeBaseUrl:
        this.template?.knowledgeBaseUrl ||
        this.overviewForm.value.knowledgeBaseUrl ||
        '',
      comment: '',
      // Add required fields for IServiceChangeRequest
      serviceManagerId: '',
      qaReviewerId: '',
      type: 'NEW' as any,
      organizationId: '',
      dateCreated: new Date().toISOString(),
      lastModifiedDate: new Date().toISOString(),
      deactivated: false,
      version: 1,
    } as IServiceChangeRequest;
  }
}
